// The cells a matched-contact row is made of — shared by the desktop table row and the mobile
// card, so the two layouts can never disagree about what a row says or offers.
import { useState } from "react";
import { Box, Button, CircularProgress, IconButton, ListItemIcon, Menu, MenuItem, Stack, Tooltip, Typography } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import CallRoundedIcon from "@material-ui/icons/CallRounded";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import FavoriteRoundedIcon from "@material-ui/icons/FavoriteRounded";
import FavoriteBorderRoundedIcon from "@material-ui/icons/FavoriteBorderRounded";
import MoreVertRoundedIcon from "@material-ui/icons/MoreVertRounded";
import EditRoundedIcon from "@material-ui/icons/EditRounded";
import OpenInNewRoundedIcon from "@material-ui/icons/OpenInNewRounded";
// A plain arrow leaving the corner. `OpenInFull` (four corners pointing out) reads as
// "maximise" and looked wrong inline beside a name; `OpenInNew` means a new TAB, which this is not.
import CallMadeIcon from "@material-ui/icons/CallMade";
import VisibilityRoundedIcon from "@material-ui/icons/VisibilityRounded";
import WarningAmberRoundedIcon from "@material-ui/icons/WarningAmberRounded";
import CancelRoundedIcon from "@material-ui/icons/CancelRounded";
import ExpandMoreRoundedIcon from "@material-ui/icons/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@material-ui/icons/ExpandLessRounded";
import Label from "components/Label";
import { ContactTableAvatar } from "../../contacts/table/rows/ContactTableAvatar";
import { ContactCrmStatusChip } from "../../contacts/table/rows/ContactCrmStatusChip";
import { formatBudget, formatContactPhone, telHref, whatsappHref } from "../../crm/crmFormat";
import { PATH_DASHBOARD } from "routes/paths";
import { matchMissingSummary, matchTimesLine, scoreBand } from "./matchRows";
import type { MatchRow } from "./matchRows";
import type { RealEstateContact } from "../../../../@types/sharedSchema";

// `SCORE_BANDS` / `scoreBand` live in the pure model (matchRows.ts) so the thresholds are
// unit-tested rather than implied by JSX.

/**
 * Avatar + name + status + phone + how fresh the lead is, and whose buyer it is for a colleague's
 * row.
 *
 * ⚠️ THE NAME IS THE CONTROL. The ROW expands the reasons panel (2026-09-30), so the contact's side
 * panel moved onto the name — with an explicit ⤢ after it, because a word that is a button without
 * looking like one is a feature nobody finds. The AVATAR is inside the control too: the click target
 * shrank from a whole row to a word, and on a phone a word is not a touch target.
 *
 * `OpenInFullIcon` rather than `OpenInNewRoundedIcon`: this opens a side PANEL, not a tab, and
 * `CrmContactPanel.tsx:569-578` already uses that icon for exactly this meaning. The new-tab icon
 * stays on "כרטיס הלקוח" in the ⋮ menu, which really does open a tab.
 *
 * A REDACTED colleague row has no identity to open, so it gets no control and no icon — it must not
 * look clickable.
 */
export function MatchIdentity({
  row,
  dense,
  compact,
  onOpenPanel
}: {
  row: MatchRow;
  dense?: boolean;
  /** Narrow CONTAINER: drop the freshness line and give the name a real touch target. */
  compact?: boolean;
  /** Absent ⇒ there is nothing to open (a redacted colleague row). */
  onOpenPanel?: (row: MatchRow) => void;
}): JSX.Element {
  const openable = Boolean(onOpenPanel);
  const timesLine = matchTimesLine(row);

  const avatar = (
    <ContactTableAvatar
      avatarSize={dense ? 32 : 36}
      badgeSize={16}
      callerFullName={row.access === "redacted" ? row.ownerName ?? "?" : row.displayName}
      avatarUrl={row.access === "redacted" ? row.ownerAvatar : undefined}
      crmIsHot={row.isHot}
    />
  );

  const nameText = (
    <Box
      component="span"
      sx={{
        fontWeight: 600,
        color: row.access === "redacted" ? "text.secondary" : "text.primary",
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }}
    >
      {row.displayName}
    </Box>
  );

  const identity = openable ? (
    <Tooltip title="פתיחת כרטיס הלקוח">
      <Box
        role="button"
        tabIndex={0}
        aria-label={`פתיחת כרטיס הלקוח ${row.displayName}`}
        // Stops the row's own click, which would otherwise expand the reasons panel at the same time.
        onClick={(event) => {
          event.stopPropagation();
          onOpenPanel?.(row);
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          event.stopPropagation();
          onOpenPanel?.(row);
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          minWidth: 0,
          cursor: "pointer",
          borderRadius: 1,
          // A real touch target on a phone, without moving the text off its baseline.
          ...(compact ? { py: 0.5, my: -0.5, minHeight: 32 } : {}),
          "&:hover .match-identity-name": { textDecoration: "underline" },
          "&:focus-visible": { outline: (theme) => `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 }
        }}
      >
        {avatar}
        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0 }}>
          <Box component="span" className="match-identity-name" sx={{ minWidth: 0, display: "flex" }}>
            {nameText}
          </Box>
          <CallMadeIcon sx={{ fontSize: 13, color: "text.disabled", flexShrink: 0 }} />
        </Stack>
      </Box>
    </Tooltip>
  ) : (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
      {avatar}
      {nameText}
    </Stack>
  );

  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
      <Stack sx={{ minWidth: 0, flexGrow: 1 }}>
        {/* Wraps so a long name never pushes the status chip off the row: the chip is the thing a
            realtor triages on, and it must not be the thing that disappears. */}
        <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" sx={{ minWidth: 0 }}>
          <Typography variant="body2" component="div" sx={{ minWidth: 0 }}>
            {identity}
          </Typography>
          <Box sx={{ flexShrink: 0 }}>
            <ContactCrmStatusChip contact={row.contact as RealEstateContact} />
          </Box>
        </Stack>
        {row.contactId && (
          <Typography variant="caption" color="text.secondary" dir="ltr" sx={{ textAlign: "right" }} noWrap>
            {formatContactPhone(row.contactId)}
          </Typography>
        )}
        {/* Provenance, so it must not compete with the phone above it. Dropped entirely when the
            container is narrow — it is the least important line on the row. */}
        {!compact && timesLine && (
          <Typography variant="caption" color="text.disabled" noWrap>
            {timesLine}
          </Typography>
        )}
        {/* An office's record nobody has on their list (`officeOnly`) is with no realtor at all. */}
        {row.officeOnly && (
          <Typography variant="caption" color="text.secondary" noWrap>
            לא אצל אף מתווך
          </Typography>
        )}
        {!row.officeOnly && row.access !== "own" && row.access !== "redacted" && row.ownerName && (
          <Typography variant="caption" color="text.secondary" noWrap>
            אצל {row.ownerName}
          </Typography>
        )}
        {!row.officeOnly && row.access === "redacted" && (
          <Typography variant="caption" color="text.disabled" noWrap>
            פרטים אצל הסוכן
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

/**
 * The match % as the row's headline: a bold number inside a ring that fills to the score, coloured
 * by the score's own band (`SCORE_BANDS`). It used to be a grey caption beside the tier label —
 * "almost too easy to miss" (2026-09-18), on the one number the table is sorted by and
 * "בחירת כל ההתאמות מעל X%" selects on.
 */
export function MatchScore({ row, size = 42 }: { row: MatchRow; size?: number }): JSX.Element {
  const value = Math.max(0, Math.min(100, Math.round(row.confidence)));
  const band = scoreBand(value);
  // `default` has no palette entry for CircularProgress, so the ring falls back to the neutral
  // `inherit` and the Box below colours it grey.
  const tierColor = band.color === "default" ? undefined : band.color;
  return (
    <Tooltip title={`${value}% · ${band.label}`}>
      <Box sx={{ position: "relative", display: "inline-flex", flexShrink: 0, width: size, height: size }}>
        {/* The track, so a low score still reads as a ring and not a stray arc. */}
        <CircularProgress
          variant="determinate"
          value={100}
          size={size}
          thickness={4.5}
          sx={{ position: "absolute", inset: 0, color: (theme) => alpha(theme.palette.grey[500], 0.18) }}
        />
        <CircularProgress
          variant="determinate"
          value={Math.max(value, 2)}
          size={size}
          thickness={4.5}
          color={tierColor ?? "inherit"}
          sx={{
            position: "absolute",
            inset: 0,
            ...(tierColor ? {} : { color: "text.disabled" }),
            "& circle": { strokeLinecap: "round" }
          }}
        />
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography component="span" dir="ltr" sx={{ fontWeight: 800, fontSize: size >= 40 ? 13 : 11.5, lineHeight: 1, color: "text.primary" }}>
            {value}%
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
}

/**
 * "אין מספיק נתונים על הלקוח" — the ONE thing a realtor has to know before reading the % on a row
 * whose contact card carries only a budget and an area (server: the `dataDepth` reason). Without
 * it the score reads as a verdict on the match when it is really a verdict on two fields.
 */
/**
 * What the customer asked for and this listing does not deliver — on the COLLAPSED row, where the
 * realtor decides whom to call: "חסר: ממ\"ד, גינה" or "חסר: ממ\"ד, סורגים ועוד 4".
 *
 * It was only ever visible inside "פרטים נוספים", so scanning the list told the realtor nothing
 * about the gaps — reported against an 89% row whose customer had asked for a garden the listing
 * never mentions. Two names then a count; the full list is one click away.
 *
 * RED when the listing says it hasn't got something, AMBER when it simply never says. "The ad
 * doesn't mention a garden" is a question for the seller, not a verdict, and colouring both the same
 * red would put a red badge on a row for something nobody has actually ruled out. The distinction
 * is per-row, not per-item: if any one of them is a flat contradiction the badge is red.
 */
export function MatchMissingBadge({ row }: { row: MatchRow }): JSX.Element | null {
  const summary = matchMissingSummary(row.reasons);
  if (!summary) return null;
  const listingSaysNo = row.reasons.some((reason) => reason.status === "mismatch");
  return (
    <Tooltip title={listingSaysNo ? "הלקוח ביקש, והנכס לא מספק את זה" : "הלקוח ביקש, והמודעה לא מציינת שיש — שווה לברר מול המוכר"}>
      <Box component="span" sx={{ display: "inline-flex", maxWidth: "100%" }}>
        <Label color={listingSaysNo ? "error" : "warning"} variant="ghost" sx={{ gap: 0.5, maxWidth: "100%" }}>
          {listingSaysNo ? <CancelRoundedIcon sx={{ fontSize: 14 }} /> : <WarningAmberRoundedIcon sx={{ fontSize: 14 }} />}
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: "22px" }}>
            {summary}
          </Box>
        </Label>
      </Box>
    </Tooltip>
  );
}

export function MatchDataWarning({ row }: { row: MatchRow }): JSX.Element | null {
  if (!row.needsMoreContactData) return null;
  return (
    <Tooltip title="ידועים רק תקציב ואזור — פתחו ״פרטים נוספים״ כדי לראות מה חסר">
      <Box component="span" sx={{ display: "inline-flex", maxWidth: "100%" }}>
        <Label color="warning" variant="ghost" sx={{ gap: 0.5, maxWidth: "100%" }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 14 }} />
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: "22px" }}>
            אין מספיק נתונים על הלקוח
          </Box>
        </Label>
      </Box>
    </Tooltip>
  );
}

/**
 * "התאמה חלשה" — this row contradicts something the customer asked for and is only on screen
 * because the realtor ticked the box. It has to say so ON the row: the realtor opted in once, at
 * the top, and would otherwise read these as ordinary matches.
 */
export function MatchLooseBadge({ row }: { row: MatchRow }): JSX.Element | null {
  if (!row.isLoose) return null;
  return (
    <Tooltip title="משהו שהלקוח ביקש לא מתקיים בנכס — פתחו ״פרטים נוספים״ כדי לראות מה">
      <Box component="span" sx={{ display: "inline-flex", maxWidth: "100%" }}>
        <Label color="error" variant="ghost" sx={{ gap: 0.5, maxWidth: "100%" }}>
          <CancelRoundedIcon sx={{ fontSize: 14 }} />
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: "22px" }}>
            התאמה חלשה
          </Box>
        </Label>
      </Box>
    </Tooltip>
  );
}

/** The row's one control for its reasons: a text button, never a wall of chips. */
export function MatchDetailsToggle({
  open,
  gaps,
  onClick
}: {
  open: boolean;
  /** How many criteria are off or unknown — so the realtor knows whether opening is worth it. */
  gaps: number;
  onClick: () => void;
}): JSX.Element {
  return (
    <Button
      size="small"
      color="inherit"
      onClick={onClick}
      endIcon={open ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
      sx={{ px: 0.75, typography: "caption", fontWeight: 600, color: "text.secondary", whiteSpace: "nowrap" }}
    >
      {/* The count is the useful half: "פרטים נוספים" alone says nothing about whether to look. */}
      {open ? "סגירת הפרטים" : gaps > 0 ? `פרטים נוספים (${gaps})` : "פרטים נוספים"}
    </Button>
  );
}

export function MatchBudgetCell({ row }: { row: MatchRow }): JSX.Element {
  const text = formatBudget(row.budget);
  return (
    <Typography variant="body2" color={text ? "text.primary" : "text.disabled"} noWrap>
      {text || "—"}
    </Typography>
  );
}

/** "במיניסייט" and how many times the customer opened this listing there. */
export function MatchMinisiteCell({ inMinisite, views }: { inMinisite: boolean; views?: number }): JSX.Element {
  if (!inMinisite) {
    return (
      <Typography variant="caption" color="text.disabled">
        —
      </Typography>
    );
  }
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Label color="info">במיניסייט</Label>
      {views ? (
        <Tooltip title={`הלקוח צפה בנכס ${views} פעמים`}>
          <Stack direction="row" alignItems="center" spacing={0.25} sx={{ color: "success.main" }}>
            <VisibilityRoundedIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {views}
            </Typography>
          </Stack>
        </Tooltip>
      ) : null}
    </Stack>
  );
}

type ActionsProps = {
  row: MatchRow;
  inMinisite: boolean;
  /** Views of this listing in the contact's minisite — shown beside the heart when given. */
  views?: number;
  busy?: boolean;
  onToggleMinisite: (row: MatchRow) => void;
  onShareWhatsApp: (row: MatchRow) => void;
  onEditInMinisite: (row: MatchRow) => void;
};

/**
 * The per-row actions. A colleague's REDACTED row offers the only useful thing — reaching the
 * colleague who holds the buyer — and nothing that would need the buyer's identity.
 */
export function MatchRowActions({ row, inMinisite, views, busy, onToggleMinisite, onShareWhatsApp, onEditInMinisite }: ActionsProps): JSX.Element {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  if (row.access === "redacted") {
    return (
      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
        {row.ownerPhone && (
          <>
            <Tooltip title={`שיחה ל${row.ownerName ?? "סוכן"}`}>
              <IconButton size="small" href={telHref(row.ownerPhone)}>
                <CallRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={`וואטסאפ ל${row.ownerName ?? "סוכן"}`}>
              <IconButton size="small" href={whatsappHref(row.ownerPhone)} target="_blank" rel="noopener">
                <WhatsAppIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={0.25} justifyContent="flex-end" alignItems="center">
      {views ? (
        <Tooltip title={`הלקוח צפה בנכס במיניסייט ${views} פעמים`}>
          <Stack direction="row" alignItems="center" spacing={0.25} sx={{ color: "success.main", px: 0.5 }}>
            <VisibilityRoundedIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {views}
            </Typography>
          </Stack>
        </Tooltip>
      ) : null}
      <Tooltip title={inMinisite ? "במיניסייט · לחיצה מסירה" : "הוספה למיניסייט"}>
        <span>
          <IconButton size="small" disabled={busy} onClick={() => onToggleMinisite(row)}>
            {/* A spinner IN PLACE of the heart, not merely a disabled heart. Adding a listing to a
                minisite is a round trip (it now also fetches the realtor's presentation for the
                listing), and a heart that simply stops responding reads as a dead button — reported
                2026-09-30, "right now we don't do anything, looks weird". Sized to the icon so the
                row does not reflow while it spins. */}
            {busy ? (
              <CircularProgress size={18} thickness={5} color="inherit" />
            ) : inMinisite ? (
              <FavoriteRoundedIcon fontSize="small" color="error" />
            ) : (
              <FavoriteBorderRoundedIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="שליחת הנכס בוואטסאפ">
        <IconButton size="small" onClick={() => onShareWhatsApp(row)}>
          <WhatsAppIcon fontSize="small" sx={{ color: "#25D366" }} />
        </IconButton>
      </Tooltip>
      {row.contactId && (
        <Tooltip title="התקשרות">
          <IconButton size="small" href={telHref(row.contactId)}>
            <CallRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <IconButton size="small" aria-label="עוד פעולות" onClick={(event) => setMenuAnchor(event.currentTarget)}>
        <MoreVertRoundedIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          disabled={!inMinisite}
          onClick={() => {
            setMenuAnchor(null);
            onEditInMinisite(row);
          }}
        >
          <ListItemIcon>
            <EditRoundedIcon fontSize="small" />
          </ListItemIcon>
          עריכת הנכס במיניסייט
        </MenuItem>
        {row.access === "own" && row.contactId && (
          <MenuItem
            component="a"
            href={PATH_DASHBOARD.general.contactDetail(row.contactId)}
            target="_blank"
            rel="noopener"
            onClick={() => setMenuAnchor(null)}
          >
            <ListItemIcon>
              <OpenInNewRoundedIcon fontSize="small" />
            </ListItemIcon>
            כרטיס הלקוח
          </MenuItem>
        )}
      </Menu>
    </Stack>
  );
}
